/**
 * shortcuts.ts — the one list of keyboard shortcuts.
 *
 * The ? overlay, the command palette hints and the global key handler in
 * App.tsx all read from here, so a shortcut is only shown if it works.
 * Keys are matched on `KeyboardEvent.code` for digits so Alt+1 also works
 * on layouts where Alt/Option+digit types a character.
 */
import type { View } from '../App';
import { byRecency } from './chatFormat';

export type ShortcutId =
  | 'palette' | 'shortcuts' | 'settings'
  | 'go:today' | 'go:chat' | 'go:mail' | 'go:teams' | 'go:meet' | 'go:files' | 'go:calendar' | 'go:workflows'
  | 'room:prev' | 'room:next' | 'room:nextUnread';

export interface Shortcut {
  id: ShortcutId;
  /** Display keys; 'Mod' renders as ⌘ on Apple platforms and Ctrl elsewhere */
  keys: string[];
  group: 'general' | 'navigation' | 'chat';
  /** i18n key under shortcuts.* */
  label: string;
  /** Works while focus is in a text field (only modifier chords qualify) */
  inInputs?: boolean;
  view?: View;
}

export const NAV_ORDER: View[] = ['today', 'chat', 'mail', 'teams', 'meet', 'files', 'calendar', 'workflows'];

export const SHORTCUTS: Shortcut[] = [
  { id: 'palette', keys: ['Mod', 'K'], group: 'general', label: 'palette', inInputs: true },
  { id: 'shortcuts', keys: ['?'], group: 'general', label: 'shortcuts' },
  { id: 'settings', keys: ['Mod', ','], group: 'general', label: 'settings', inInputs: true },
  ...NAV_ORDER.map((view, i): Shortcut => ({
    id: `go:${view}` as ShortcutId, keys: ['Alt', String(i + 1)], group: 'navigation', label: `go.${view}`, inInputs: true, view,
  })),
  { id: 'room:prev', keys: ['Alt', '↑'], group: 'chat', label: 'roomPrev', inInputs: true },
  { id: 'room:next', keys: ['Alt', '↓'], group: 'chat', label: 'roomNext', inInputs: true },
  { id: 'room:nextUnread', keys: ['Alt', 'Shift', '↓'], group: 'chat', label: 'roomNextUnread', inInputs: true },
];

/** Shortcuts handled by the editor or a panel, listed for reference only */
export const LOCAL_SHORTCUTS: { keys: string[]; group: Shortcut['group']; label: string }[] = [
  { keys: ['Enter'], group: 'chat', label: 'send' },
  { keys: ['Shift', 'Enter'], group: 'chat', label: 'newLine' },
  { keys: ['Mod', 'F'], group: 'chat', label: 'searchInChat' },
  { keys: ['Mod', 'B'], group: 'chat', label: 'bold' },
  { keys: ['Mod', 'I'], group: 'chat', label: 'italic' },
  { keys: ['Esc'], group: 'general', label: 'close' },
];

export const isApple = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export function displayKey(k: string): string {
  if (k === 'Mod') return isApple() ? '⌘' : 'Ctrl';
  if (k === 'Alt') return isApple() ? '⌥' : 'Alt';
  if (k === 'Shift') return isApple() ? '⇧' : 'Shift';
  return k;
}

export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

/** Returns the shortcut a key event triggers, or null. */
export function matchShortcut(e: KeyboardEvent): ShortcutId | null {
  const mod = e.ctrlKey || e.metaKey;
  const typing = isTypingTarget(e.target);
  let id: ShortcutId | null = null;

  if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') id = 'palette';
  else if (mod && !e.altKey && e.key === ',') id = 'settings';
  else if (e.altKey && !mod && !e.shiftKey && /^Digit[1-9]$/.test(e.code)) {
    const view = NAV_ORDER[Number(e.code.slice(5)) - 1];
    if (view) id = `go:${view}` as ShortcutId;
  }
  else if (e.altKey && !mod && e.key === 'ArrowUp' && !e.shiftKey) id = 'room:prev';
  else if (e.altKey && !mod && e.key === 'ArrowDown') id = e.shiftKey ? 'room:nextUnread' : 'room:next';
  else if (e.key === '?' && !mod && !e.altKey) id = 'shortcuts';

  if (!id) return null;
  if (typing && !SHORTCUTS.find(s => s.id === id)?.inInputs) return null;
  return id;
}

interface RoomLike { id: string; unreadCount?: number }

/**
 * Next room for Alt+↑/↓ and Alt+Shift+↓, in the order the room list shows.
 * Wraps around; returns null when there is nowhere to go.
 */
export function pickRoom(rooms: RoomLike[], activeId: string | null, action: 'room:prev' | 'room:next' | 'room:nextUnread'): string | null {
  if (rooms.length === 0) return null;
  const i = rooms.findIndex(r => r.id === activeId);
  if (action === 'room:nextUnread') {
    for (let step = 1; step <= rooms.length; step++) {
      const r = rooms[(Math.max(i, -1) + step + rooms.length) % rooms.length]!;
      if (r.id !== activeId && (r.unreadCount ?? 0) > 0) return r.id;
    }
    return null;
  }
  if (i === -1) return rooms[0]!.id;
  const next = (i + (action === 'room:next' ? 1 : -1) + rooms.length) % rooms.length;
  return next === i ? null : rooms[next]!.id;
}

/** Room list order: channels and groups first, then direct messages, each newest first */
export function roomListOrder<T extends { type: string; lastMessage?: { at: string } | null }>(rooms: T[]): T[] {
  return [...byRecency(rooms.filter(r => r.type !== 'direct')), ...byRecency(rooms.filter(r => r.type === 'direct'))];
}
